import { isIsccStandard } from '../utils/isccStandards';

// Per-document save-time validation.
//
// `getDocumentFormFieldErrors` is the real implementation and returns a
// { fieldName: message } map so the form can mark the offending control
// invalid and scroll to it. `getDocumentFormValidationError` is kept as the
// original single-string API (first error wins) because `handleSave` and the
// existing tests both depend on that exact shape and message ordering.
//
// Note on scope: only fields the produced document genuinely cannot be issued
// without belong here.

const FAPP_01 = 'FAPP-01 Audit Plan';
const FTRD_02 = 'FTRD-02 Certificate Review and Production';

const isBlank = (value) => !String(value ?? '').trim();

// ISCC certificates carry Valid From / Valid To; every other scheme carries the
// Initial / Latest / Issue / Expired set. Detection is shared with the form and the
// renderer (utils/isccStandards) so validation follows whichever date pair the
// operator was actually shown.
const isIsccCertificateForm = (formData = {}) => {
  const standards = Array.isArray(formData.applicableStandards)
    ? formData.applicableStandards
    : String(formData.applicableStandards || '').split(',');
  return standards.some(isIsccStandard);
};

const getFapp01FieldErrors = (formData) => {
  const errors = {};
  if (isBlank(formData.issueDate)) {
    errors.issueDate = 'Issue Date is required.';
  }
  if (isBlank(formData.auditStartDate)) {
    errors.auditStartDate = 'Audit Start Date is required.';
  }
  if (isBlank(formData.auditEndDate)) {
    errors.auditEndDate = 'Audit End Date is required.';
  } else if (formData.auditEndDate < formData.auditStartDate) {
    errors.auditEndDate = 'Audit End Date must be on or after Audit Start Date.';
  }
  return errors;
};

const getFtrd02FieldErrors = (formData) => {
  const errors = {};
  if (isBlank(formData.certificateNumber)) {
    errors.certificateNumber = 'Certificate Number is required.';
  }
  if (isBlank(formData.clientName)) {
    errors.clientName = 'Client / Company Name is required.';
  }
  if (isBlank(formData.certificateScope)) {
    errors.certificateScope = 'Certificate Scope is required.';
  }

  if (isIsccCertificateForm(formData)) {
    if (!isBlank(formData.validFrom) && !isBlank(formData.validTo)
      && formData.validTo < formData.validFrom) {
      errors.validTo = 'Valid To must be on or after Valid From.';
    }
  } else if (!isBlank(formData.issueDate) && !isBlank(formData.expiredDate)
    && formData.expiredDate < formData.issueDate) {
    errors.expiredDate = 'Expired Date must be on or after Issue Date.';
  }

  return errors;
};

export const getDocumentFormFieldErrors = (docCode, formData = {}) => {
  if (docCode === FAPP_01) return getFapp01FieldErrors(formData);
  if (docCode === FTRD_02) return getFtrd02FieldErrors(formData);
  return {};
};

// Field order here is the order errors are reported in — the existing FAPP-01
// tests pin these exact messages and this exact precedence.
const FIELD_ORDER = [
  'issueDate',
  'auditStartDate',
  'auditEndDate',
  'certificateNumber',
  'clientName',
  'certificateScope',
  'validTo',
  'expiredDate',
];

// The field the reported message belongs to, so the form scrolls to the same
// field the alert is talking about rather than to whichever key happens to
// come first in the error object.
export const getFirstInvalidDocumentFormField = (docCode, formData = {}) => {
  const errors = getDocumentFormFieldErrors(docCode, formData);
  return FIELD_ORDER.find((field) => errors[field])
    || Object.keys(errors).find((field) => errors[field])
    || '';
};

export const getDocumentFormValidationError = (docCode, formData = {}) => {
  const errors = getDocumentFormFieldErrors(docCode, formData);
  const firstField = getFirstInvalidDocumentFormField(docCode, formData);
  return firstField ? errors[firstField] : '';
};
